import { defineStore } from 'pinia';
import {
  EntityConfigurationErrors,
  getInitialEntityState,
} from '@/store/common/entities-state';
import { createEntityActions } from '@/store/common/entities-actions';
import { Collection } from '@/store/common/collections';
import { createEntityGetters } from '@/store/common/entities-getters';
import { Employee } from '@/model/employee';
import i18n from '@/config/i18n';
import { Service } from '@/model/service';
import escape from 'lodash-es/escape';
import { useServicesStore } from '@/store/services-store';
import map from 'lodash-es/map';
import { useVendorsStore } from '@/store/vendors-store';
import { InputOption } from '@/model/input-option';
import cloneDeep from 'lodash-es/cloneDeep';
import { reactive } from 'vue';
import { useUiStore } from '@/store/ui-store';
import employeesService from '@/services/employee.service';
import { hasLeft } from '@/helpers/employee.helper';
import { Language } from '@/constants';

const storeId = 'employees';

function servicesWithoutSharedWorkplace(
  employee: Employee,
  services: Service[],
): Service[] {
  const assignedServiceIds = map(employee.services, 'id');
  const assignedWorkplaceIds = map(employee.workplaces, 'id');

  return services.filter(
    (service) =>
      assignedServiceIds.includes(service.id) &&
      !service.workplaces.some((workplace) =>
        assignedWorkplaceIds.includes(workplace.id),
      ),
  );
}

function escapedServiceName(service: Service): string {
  const locale = i18n.global.locale.value as Language;

  return escape(service.article._translations[locale]?.name);
}

function sortByName(a: Employee, b: Employee): number {
  return `${a.firstname} ${a.lastname}`.localeCompare(
    `${b.firstname} ${b.lastname}`,
  );
}

function withTimetable(employees: Employee[]): Employee[] {
  return employees.filter((employee) => employee.timetable);
}

function firstWithTimetable(
  employees: Employee[],
  employeeId: number | null,
): Employee | undefined {
  const candidates = withTimetable(employees);

  return (
    candidates.find((candidate) => candidate.id === employeeId) ?? candidates[0]
  );
}
let initialEntityState = reactive({
  ...getInitialEntityState<Employee>(sortByName),
  departed: [] as Employee[],
});
const initialStateClone = cloneDeep(initialEntityState);
if (localStorage.getItem(storeId)) {
  initialEntityState = reactive({
    ...initialEntityState,
    ...JSON.parse(localStorage.getItem(storeId)),
  });
}
export const useEmployeesStore = defineStore(storeId, {
  state: () => initialEntityState,
  getters: {
    ...createEntityGetters<Employee>(),
    getAllForServiceAndLocationAsSelectOptions:
      (state) =>
      (serviceId: number, locationId: number | null = null): InputOption[] => {
        return state.entities
          .filter(
            (employee) =>
              employee.services.some(
                (service: Service) => service.id === serviceId,
              ) &&
              (!locationId ||
                employee.workplaces.some(
                  (workplace) => workplace.location_id === locationId,
                )),
          )
          .map((employee) => ({
            value: employee.id,
            label: employee.firstname + ' ' + employee.lastname,
          }));
      },
    getAllAsSelectOptions: (state) => (): InputOption[] => {
      return withTimetable(state.entities).map((employee) => {
        return {
          label: employee.firstname + ' ' + employee.lastname,
          value: employee.id,
        };
      });
    },
    getAllWithTimetablesEnabled:
      (state) =>
      (withDeparted = false): Employee[] => {
        return withTimetable(
          withDeparted
            ? [...state.entities, ...state.departed]
            : state.entities,
        );
      },
  },
  actions: {
    ...createEntityActions<Employee>(Collection.EMPLOYEES, initialEntityState),
    async findDeparted() {
      try {
        this.departed = (await employeesService.getAllWithLeft()).filter(
          hasLeft,
        );
      } catch (exception) {
        useUiStore().alert(exception.message);
      }
    },
    async invite(employee: Employee, email: string) {
      await employeesService.invite(employee.id, email);
    },
    async leave(employee: Employee, lastDay: string) {
      await employeesService.leave(employee.id, lastDay);
      await this.refreshMembership(employee.id);
    },
    async cancelLeave(employee: Employee) {
      await employeesService.cancelLeave(employee.id);
      await this.refreshMembership(employee.id);
    },
    async refresh() {
      await this.findAll(null, true, false);
      this.syncCurrentEntity(this.currentEntity.id);
    },
    async refreshMembership(employeeId: number) {
      await Promise.all([this.findAll(null, true, false), this.findDeparted()]);
      this.syncCurrentEntity(employeeId);
    },
    setCurrentEntityWithTimetable(employeeId: number | null) {
      const employee = firstWithTimetable(this.entities, employeeId);
      if (employee) {
        this.setCurrentEntity(employee);
      } else {
        this.unsetCurrentEntity();
      }
    },
    keepCurrentEntityWithTimetable(employeeId: number | null) {
      if (!this.currentEntity?.timetable) {
        this.setCurrentEntityWithTimetable(employeeId);
      }
    },
    syncCurrentEntity(employeeId: number) {
      this.currentEntity =
        [...this.entities, ...this.departed].find(
          (employee: Employee) => employee.id === employeeId,
        ) ?? this.currentEntity;
    },
    verifyConfiguration() {
      const employeesErrors: EntityConfigurationErrors[] = [];
      this.entities.forEach((employee: Employee) => {
        const entityErrors: EntityConfigurationErrors = {
          id: employee.id,
          errors: [],
        };
        // On ne gère pas les employés qui n'ont pas d'horaires
        if (employee.timetable) {
          const vendorsStore = useVendorsStore();
          const servicesStore = useServicesStore();
          // Assignation par rapport à un espace de travail + service au minimum
          if (employee.services.length === 0) {
            entityErrors.errors.push(
              i18n.global.t('configError.employeeServices'),
            );
          }
          if (vendorsStore.currentEntity.workplaces_enabled) {
            if (employee.workplaces.length === 0) {
              entityErrors.errors.push(
                i18n.global.t('configError.employeeWorkplaces'),
              );
            } else {
              const unbookableServices = servicesWithoutSharedWorkplace(
                employee,
                servicesStore.entities,
              );
              if (unbookableServices.length > 0) {
                entityErrors.errors.push(
                  i18n.global.t(
                    'configError.employeeServicesWorkplaces',
                    {
                      services: unbookableServices
                        .map(escapedServiceName)
                        .join(', '),
                    },
                    unbookableServices.length,
                  ),
                );
              }
            }
          }
          /**
           * TODO : a des horaires dans un lieu où aucun de ses services ne peut être effectué (il faudra refactor employees module et tout passer dans resources module pour être propre) => on que l'horaire de l'employé courant
           */
        }
        if (entityErrors.errors.length > 0) {
          employeesErrors.push(entityErrors);
        }
      });

      this.setConfigurationErrors(employeesErrors);
    },
    reset() {
      this.$state = Object.assign(
        {},
        this.$state,
        cloneDeep(initialStateClone),
      );
    },
  },
});
